import bindAllOfThis from "src/utils/BindThisHelper";
import { Color } from "./ColorValue";
import TranslateApi from "src/utils/TranslateHelper";

export class PhaserButtonConfig {
    public backgroundColor: number = Color.KUDER_PRIMARY_BUTTON;
    public smartSize?: boolean = false;
    public smartHeight?: boolean = false;
    public keyMapping?: string; // string representation of the keyboard key you want to map this button to: E.g. keydown_RIGHT

    public static BackButtonConfig: PhaserButtonConfig = {
        backgroundColor: Color.KUDER_SECONDARY_BUTTON,
        smartSize: true,
        smartHeight: true,
        keyMapping: "keydown_LEFT"
    };

    public static NextButtonConfig: PhaserButtonConfig = {
        backgroundColor: Color.KUDER_SECONDARY_BUTTON,
        smartSize: true,
        smartHeight: true,
        keyMapping: "keydown_RIGHT"
    };
}

export class PhaserButton extends Phaser.GameObjects.Container {
    protected text: string;
    config: PhaserButtonConfig;
    protected textObject: Phaser.GameObjects.Text;
    protected backgroundObject: Phaser.GameObjects.Image;
    protected border: Phaser.GameObjects.Image;
    protected padding: number = 10;

    constructor(scene: Phaser.Scene, x: number, y: number, width: number, height: number, text?: string, config?: PhaserButtonConfig) {
        super(scene, x, y);
        this.width = width;
        this.height = height;
        this.config = config;
        this.text = text;

        if (config == null) {
            this.config = new PhaserButtonConfig();
        }

        bindAllOfThis(this, PhaserButton.prototype);
        this.initialize(this.width, this.height);
    }

    public static createQuickNextButton(scene, x, y): PhaserButton {
        var button = new PhaserButton(scene, x, y, 300, 100, TranslateApi.translate("common.next"), PhaserButtonConfig.NextButtonConfig);
        return button;
    }

    public static createQuickBackButton(scene, x, y): PhaserButton {
        var button = new PhaserButton(scene, x, y, 200, 100, TranslateApi.translate("common.back"), PhaserButtonConfig.BackButtonConfig);
        return button;
    }

    static preload(scene: Phaser.Scene) {
        scene.load.image("pixel", require('/src/assets/qci/sprites/pixel.png'));
    }

    initialize(width: number, height: number): void {
        // Set size
        this.setSize(width, height);

        // Construct Objects
        this.constructObjects();
        this.type = "PhaserButton";

        // Add this modal to the scene
        this.scene.add.existing(this);
    }

    constructObjects() {
        if (this.text != null) {
            // Button text
            this.textObject = this.scene.add.text(0, 0, this.text);
            this.textObject.setSize(this.width, this.height);
            this.textObject.setFontSize(18);
            this.textObject.setTint(0xffffff);
            this.textObject.setFontFamily('Russo One, Arial');
            this.textObject.setWordWrapWidth(this.width - 2 * this.padding, true);
            this.textObject.setAlign('center');
            this.textObject.setScrollFactor(0);
            // Center text to the background
            this.textObject.setOrigin(0.5);
            this.textObject.setPosition(this.width / 2, this.height / 2);
        }

        if (this.config.smartSize) {
            this.width = this.getIdealButtonWidth();
            this.height = this.getIdealButtonHeight();
            this.textObject.setPosition(this.width / 2, this.height / 2);
        }
        else if (this.config.smartHeight) {
            this.height = this.getIdealButtonHeight();
            this.textObject.setPosition(this.width / 2, this.height / 2);
        }

        // Border of the button
        this.border = this.scene.add.image(-5, -5, "pixel");
        this.border.setOrigin(0, 0);
        this.border.setDisplaySize(this.width + this.padding, this.height + this.padding);
        this.border.setTint(0x000000);
        this.border.setInteractive({ useHandCursor: true });
        this.border.setScrollFactor(0);

        // Background of the button
        this.backgroundObject = this.scene.add.image(0, 0, "pixel");
        this.backgroundObject.setOrigin(0, 0);
        this.backgroundObject.setDisplaySize(this.width, this.height);
        this.backgroundObject.setTint(this.config.backgroundColor);
        this.backgroundObject.setInteractive({ useHandCursor: true });
        this.backgroundObject.setScrollFactor(0);

        // Button on click event emits a buttonClicked event (sends pointer, x, and y)
        this.backgroundObject.on('pointerdown', (pointer: Phaser.Input.Pointer, x: number, y: number) => {
            if (this.text != null) {
                this.emit('buttonClicked', pointer, x, y, this.textObject.text);
            }
            else {
                this.emit('buttonClicked', pointer, x, y);
            }
        });

        if (this.config.keyMapping != null && this.backgroundObject.input.enabled) {
            this.scene.input.keyboard.on(this.config.keyMapping, () => {
                this.emit('buttonClicked', this.textObject.text);
            });
        }

        this.add(this.border);
        this.add(this.backgroundObject);
        this.add(this.textObject);
    }


    public getIdealButtonHeight(): number {
        // Ideal button height would be the height of the actual text object
        return this.textObject.getBounds().height + 30;
    }

    public getIdealButtonWidth(): number {
        // Ideal button width would be the width of the actual text object
        return this.textObject.getBounds().width + 2 * this.padding + 15;
    }

    public setDisplaySize(width: number, height: number): this {
        this.setSize(width, height);
        this.backgroundObject.setDisplaySize(width, height);
        this.border.setDisplaySize(width + this.padding, height + this.padding);
        // Center text to the background
        this.textObject.setOrigin(0.5);
        this.textObject.setWordWrapWidth(width - 2 * this.padding, true);
        this.textObject.setPosition(width / 2, height / 2);
        return this;
    }

    /**
     * Dynamically change the button text by setting the textObject text
     */
    public setText(text: string): void {
        this.textObject.setText(text);
    }

    public setEnabled(enabled: boolean): void {
        this.backgroundObject.input.enabled = enabled;
    }


    public getBackground(): Phaser.GameObjects.Image {
        return this.backgroundObject;
    }

    public isEnabled(): boolean {
        return this.backgroundObject.input.enabled;
    }
}



// WEBPACK FOOTER //
// ./src/games/shared/Button.ts